
import { ContentItem } from "../types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export function useContentUpdate(allContent: ContentItem[], setAllContent: React.Dispatch<React.SetStateAction<ContentItem[]>>) {
  const handleChangeContentCredits = async (contentId: number, newCredits: number) => {
    if (newCredits < 1) return;
    
    try {
      // Update in Supabase
      const { error } = await supabase
        .from('videos')
        .update({ credits: newCredits })
        .eq('id', contentId);
      
      if (error) throw error;
      
      // Update local state
      setAllContent(allContent.map(item => 
        item.id === contentId 
          ? { ...item, credits: newCredits } 
          : item
      ));
      
      const contentItem = allContent.find(c => c.id === contentId);
      
      toast({
        title: "Επιτυχία",
        description: `Τα credits για το "${contentItem?.title}" άλλαξαν σε ${newCredits}`
      });
    } catch (error) {
      console.error("Error updating credits:", error);
      toast({
        title: "Σφάλμα",
        description: "Παρουσιάστηκε πρόβλημα κατά την ενημέρωση των credits",
        variant: "destructive"
      });
    }
  };

  const handleTogglePremiereStatus = async (contentId: number) => {
    try {
      // Find current status
      const contentItem = allContent.find(c => c.id === contentId);
      if (!contentItem) return;
      
      const newStatus = !contentItem.isPremiere;
      
      // Update in Supabase
      const { error } = await supabase
        .from('videos')
        .update({ is_premiere: newStatus })
        .eq('id', contentId);
      
      if (error) throw error;
      
      // Update local state
      setAllContent(allContent.map(item => 
        item.id === contentId 
          ? { ...item, isPremiere: newStatus } 
          : item
      ));
      
      toast({
        title: "Επιτυχία",
        description: `Το περιεχόμενο "${contentItem.title}" ${newStatus ? 'μαρκαρίστηκε ως πρεμιέρα' : 'δεν είναι πλέον πρεμιέρα'}`
      });
    } catch (error) {
      console.error("Error updating premiere status:", error);
      toast({
        title: "Σφάλμα",
        description: "Παρουσιάστηκε πρόβλημα κατά την ενημέρωση της κατάστασης πρεμιέρας",
        variant: "destructive"
      });
    }
  };

  const handleToggleContentVisibility = async (contentId: number) => {
    try {
      // Find current status
      const contentItem = allContent.find(c => c.id === contentId);
      if (!contentItem) return;
      
      const newVisibility = !contentItem.isPublic;
      
      // Update local state since there's no direct database field for this yet
      setAllContent(allContent.map(item => 
        item.id === contentId 
          ? { ...item, isPublic: newVisibility } 
          : item
      ));
      
      toast({
        title: "Επιτυχία",
        description: `Το περιεχόμενο "${contentItem.title}" είναι πλέον ${newVisibility ? 'δημόσιο' : 'ιδιωτικό'}`
      });
    } catch (error) {
      console.error("Error updating visibility status:", error);
      toast({
        title: "Σφάλμα",
        description: "Παρουσιάστηκε πρόβλημα κατά την ενημέρωση της ορατότητας",
        variant: "destructive"
      });
    }
  };

  const handleDeleteContent = async (contentId: number) => {
    try {
      // Find content item
      const contentItem = allContent.find(c => c.id === contentId);
      if (!contentItem) return;
      
      // Delete from Supabase
      const { error } = await supabase
        .from('videos')
        .delete()
        .eq('id', contentId);
      
      if (error) throw error;
      
      // Update local state
      setAllContent(allContent.filter(item => item.id !== contentId));
      
      toast({
        title: "Επιτυχία",
        description: `Το περιεχόμενο "${contentItem.title}" διαγράφηκε επιτυχώς`
      });
    } catch (error) {
      console.error("Error deleting content:", error);
      toast({
        title: "Σφάλμα",
        description: "Παρουσιάστηκε πρόβλημα κατά τη διαγραφή του περιεχομένου",
        variant: "destructive"
      });
    }
  };

  return {
    handleChangeContentCredits,
    handleTogglePremiereStatus,
    handleToggleContentVisibility,
    handleDeleteContent
  };
}
